import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/useToast';
import { millis } from '../../publicacoes/publicacao.definitions';
import type { Publicacao } from '../../publicacoes/publicacao.types';
import { publicacaoService } from '../../publicacoes/publicacaoService';
import { PUBLICACOES_QUERY_KEYS, usePessoaLogada } from '../../publicacoes/usePublicacoes';
import { ArquivoDocumentoCampo, ESCOLHA_VAZIA, arquivoDaEscolha, descartarEnvio, escolhaCompleta } from './ArquivoDocumentoCampo';
import type { EscolhaArquivo } from './ArquivoDocumentoCampo';
import { DrawerLateral } from './DrawerLateral';

interface NovaVersaoDrawerProps {
  documento: Publicacao | null;
  onClose: () => void;
}

/** Nova versão do documento: a atual vai pro histórico; opcionalmente pede nova ciência e reanuncia. */
export function NovaVersaoDrawer({ documento, onClose }: NovaVersaoDrawerProps) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const { autor } = usePessoaLogada();
  const [escolha, setEscolha] = useState<EscolhaArquivo>(ESCOLHA_VAZIA);
  const [oQueMudou, setOQueMudou] = useState('');
  const [pedirNovaCiencia, setPedirNovaCiencia] = useState(true);
  const [anunciar, setAnunciar] = useState(true);
  const [erros, setErros] = useState<string[]>([]);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    if (documento) {
      setEscolha({ ...ESCOLHA_VAZIA, modo: documento.arquivo?.tipo === 'link' ? 'link' : 'arquivo' });
      setOQueMudou('');
      setPedirNovaCiencia(true);
      setAnunciar(documento.anunciarNoInicio ?? true);
      setErros([]);
    }
  }, [documento]);

  if (!documento) return <DrawerLateral isOpen={false} titulo="" onClose={onClose}>{null}</DrawerLateral>;
  const atual = documento.versao ?? 1;
  const data = millis(documento.anunciadoEm) ?? millis(documento.publicarEm);

  const publicar = async () => {
    const validacao: string[] = [];
    if (!escolhaCompleta(escolha, null)) {
      validacao.push(escolha.modo === 'link' ? 'Informe um link válido (https://...).' : 'Escolha o arquivo da nova versão.');
    }
    if (!oQueMudou.trim()) validacao.push('Conte o que mudou nesta versão.');
    setErros(validacao);
    if (validacao.length > 0 || !autor) return;
    setSalvando(true);
    try {
      const arquivo = arquivoDaEscolha(escolha, null);
      await publicacaoService.novaVersao(documento, { arquivo, oQueMudou, pedirNovaCiencia, anunciar }, autor);
      toast.success(`Versão ${atual + 1} publicada.`);
      onClose();
    } catch (error) {
      toast.error(`Erro ao publicar a nova versão: ${(error as { message?: string })?.message ?? 'erro desconhecido'}`);
    } finally {
      setSalvando(false);
      queryClient.invalidateQueries({ queryKey: PUBLICACOES_QUERY_KEYS.all });
    }
  };

  return (
    <DrawerLateral
      isOpen
      titulo={`Nova versão · ${documento.titulo}`}
      onClose={() => {
        if (salvando) return;
        descartarEnvio(escolha);
        onClose();
      }}
      erros={erros}
      rodape={
        <>
          <button
            type="button"
            className="btn-secondary"
            disabled={salvando}
            onClick={() => {
              descartarEnvio(escolha);
              onClose();
            }}
          >
            Cancelar
          </button>
          <button type="button" className="btn-primary" disabled={salvando} onClick={publicar}>
            {salvando ? 'Publicando...' : `Publicar v${atual + 1}`}
          </button>
        </>
      }
    >
      <p className="rounded-lg bg-raro-gray-50 px-3 py-2 text-xs text-raro-gray-600">
        Versão atual: <b>v{atual}</b>
        {data ? ` (${new Date(data).toLocaleDateString('pt-BR')})` : ''}. A nova será a <b>v{atual + 1}</b>; a atual fica no
        histórico (só quem publica vê).
      </p>
      <ArquivoDocumentoCampo publicacaoId={documento.id} valor={escolha} onChange={setEscolha} rotulo="Arquivo da nova versão *" />
      <label className="block">
        <span className="block text-sm font-medium text-raro-gray-700">O que mudou nesta versão? *</span>
        <textarea
          className="input-primary mt-1 w-full"
          rows={3}
          maxLength={500}
          placeholder="Aparece no card para o colaborador"
          value={oQueMudou}
          onChange={(event) => setOQueMudou(event.target.value)}
        />
      </label>
      {documento.ciencia?.exige && (
        <fieldset className="space-y-1 rounded-xl border border-raro-gray-200 p-4">
          <legend className="px-1 text-sm font-medium text-raro-gray-700">Ciência</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              className="h-4 w-4 rounded text-primary-600"
              checked={pedirNovaCiencia}
              onChange={(event) => setPedirNovaCiencia(event.target.checked)}
            />
            Pedir nova ciência (a da v{atual} deixa de valer)
          </label>
          <p className="text-xs text-raro-gray-500">
            Desmarque para mudanças pequenas (ex.: correção de digitação) — quem já confirmou continua ciente.
          </p>
        </fieldset>
      )}
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="h-4 w-4 rounded text-primary-600"
          checked={anunciar}
          onChange={(event) => setAnunciar(event.target.checked)}
        />
        Anunciar a nova versão na Início (volta para o topo do feed)
      </label>
    </DrawerLateral>
  );
}
